import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useDebounce } from "../useDebounce";

const TERM_PARAM = "term";

/**
 * Debounces a search term, cancels the previous in-flight search whenever a new one starts,
 * and keeps the term synced with the `term` query param so the search state can be shared via URL.
 * Also re-runs the search (without debounce) whenever `pageIndex`/`pageSize` change, since those
 * are server-side pagination params rather than user-typed input.
 * @param {(term: string, options: { cancelToken: import("axios").CancelToken, pageIndex: number, pageSize: number }) => Promise<any>} onSearch
 * @param {number} delay - Debounce delay in milliseconds (default: 500ms)
 * @param {{ pageIndex?: number, pageSize?: number }} pagination
 * @returns {{ term: string, searchedTerm: string, handleChange: Function, handleSubmit: Function, isSearching: boolean, results: object | null }}
 */
export const useCultureHubSearch = (onSearch, delay = 500, { pageIndex = 0, pageSize = 16 } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [term, setTerm] = useState(
    () => new URLSearchParams(location.search).get(TERM_PARAM) || ""
  );
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState(null);
  // term of the latest request, i.e. the one `results` belong to (term itself changes on every keystroke)
  const [searchedTerm, setSearchedTerm] = useState("");
  const debouncedTerm = useDebounce(term, delay);
  const sourceRef = useRef(null);

  const runSearch = useCallback(
    (searchTerm, { pageIndex, pageSize }) => {
      if (sourceRef.current) sourceRef.current.cancel();

      if (!onSearch) {
        setResults(null);
        return;
      }

      const source = axios.CancelToken.source();
      sourceRef.current = source;
      setIsSearching(true);
      setSearchedTerm(searchTerm);

      Promise.resolve(onSearch(searchTerm, { cancelToken: source.token, pageIndex, pageSize }))
        // getService already unwraps the axios response, so `response` is the paginated payload itself
        .then((response) => setResults(response ?? null))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          console.error("Error searching:", error);
          setResults(null);
        })
        .finally(() => {
          if (sourceRef.current === source) setIsSearching(false);
        });
    },
    [onSearch]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedTerm) {
      params.set(TERM_PARAM, debouncedTerm);
    } else {
      params.delete(TERM_PARAM);
    }

    const nextSearch = params.toString();
    if (nextSearch !== location.search.replace(/^\?/, "")) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
    }

    runSearch(debouncedTerm, { pageIndex, pageSize });

    return () => {
      if (sourceRef.current) sourceRef.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTerm, pageIndex, pageSize]);

  const handleChange = (event) => setTerm(event.target.value);

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    runSearch(term, { pageIndex, pageSize });
  };

  return { term, searchedTerm, handleChange, handleSubmit, isSearching, results };
};
